
import * as M from '@/model';
import Progress from '@/components/Progress.vue';
import Computed from '@/components/Computed.vue';
import RealmPicker from '@/components/RealmPicker.vue';
import DocumentPicker from '@/components/DocumentPicker.vue';
import DocumentSummary from '@/components/DocumentSummary.vue';
import DocumentPopover, { Goto } from '@/components/DocumentPopover.vue';
import ExpansionPanel from '@/components/ExpansionPanel.vue';
import DocumentHeader from '@/components/DocumentHeader.vue';
import api from '@/rest-api';
import { FileDocument, fileDocumentName, Realm } from '@/model';
import labels, { labelSorter } from '@/store/labels';
import { notEmpty } from '@/util/array-util';
import LabelPanel from '@/components/LabelPanel.vue';
import DocumentEditor from '@/components/DocumentEditor.vue';
import JobQueueSubscriber from '@/controllers/JobQueueSubscriber';
import mixins from '@/plugins/typed-mixins';
import { isShortSummaryGroup } from '@/util/summary-util';

export type Status = 'Approved' | 'Pending' | 'Missing' | 'Disapproved' | 'Corrected' | 'Short';

export default mixins(JobQueueSubscriber).extend({
  name: 'SummarySelectionView',
  components: {
    Progress,
    RealmPicker,
    DocumentPicker,
    DocumentSummary,
    DocumentPopover,
    ExpansionPanel,
    Computed,
    DocumentHeader,
    LabelPanel,
    DocumentEditor,
  },
  props: {
    documentId: { type: Number as () => number | undefined, required: false },
  },

  async mounted() {
    if (!this.documentId) {
      const selectedDocuments = localStorage.getItem('summarySelectionView.selectedDocuments');
      if (selectedDocuments && selectedDocuments.length > 0) {
        const docs: number[] = JSON.parse(selectedDocuments);
        this.selectedDocuments = (await Promise.all(docs.map((id: number) => api.document(id)))).filter(notEmpty);
        this.load();
      }
    }
    const filter = localStorage.getItem('summarySelectionView.filter');
    if (filter && filter.length > 0) {
      this.filter = JSON.parse(filter);
    }
  },

  watch: {
    documentId: {
      immediate: true,
      async handler(to?: number, from?: number) {
        if (to && to !== from) {
          const document = await api.document(to);
          if (document) {
            this.selectedDocuments = [document];
            this.load();
          }
        }
      },
    },
    selectedDocuments: {
      handler() {
        // store
        localStorage.setItem('summarySelectionView.selectedDocuments', JSON.stringify(this.selectedDocuments.map((doc) => doc.id)));
      },
    },
  },
  data() {
    return {
      realms: [] as Realm[],
      selectedDocuments: [] as FileDocument[],
      summaries: undefined as undefined | Promise<M.DocumentSummaries[]>,
      documentPanels: [] as number[],
      goto: undefined as undefined | Goto,
      loading: false,
      filter: { labels: [] as number[], status: [] as Status[] },
      editItem: {} as Partial<FileDocument> | undefined,
      edit: false,
      bulkJob: undefined as M.SerfJob | undefined,
      bulkProgress: undefined as M.BulkSummaryProgress | undefined,
      generateDialog: false,
      selectedDocument: undefined as M.DocumentSummaries | undefined,
      generateOptions: {
        onlyMissing: true,
        autoApprove: false,
      },
      documentJobIds: new Map<number, number>(), // documentId -> jobId
    };
  },
  computed: {
    documents(): Promise<FileDocument[]> {
      if (this.realms.length) {
        return api.documentsFilter({ realms: this.realms.filter(notEmpty).map((r) => r.id) });
      } else {
        return Promise.resolve([]);
      }
    },
  },
  methods: {
    async load() {
      this.loading = true;
      const spec = this.spec();
      this.summaries = Promise.all(this.selectedDocuments.map((doc) => api.documentSummaries(doc.id, true, spec)));

      // Check for active bulk jobs for all selected documents
      await this.checkForActiveBulkJobs();

      this.summaries.then((summaries) => {
        this.loading = false;
        summaries.forEach((ds) => sortGroups(ds));
        if (summaries.length === 1) {
          this.documentPanels = [0];
        }
      });
    },
    spec(): M.SummarySpec {
      return {
        members: [
          [labels.getters.type('Workflow').get('Publish')?.id || 0],
          labels.getters.type('Threat').map((l) => l.id),
          labels.getters.type('Theme').filter((l) => l.code !== 'Modus Operandi').map((l) => l.id),
        ],
        include: labels.getters.type('Level').map((l) => l.id),
      };
    },
    relatedLimitation() {
      // for a threat in a document
      return {
        ls: [labels.getters.type('Threat').map((l) => l.id)],
      };
    },
    relatedSummarires(members: readonly number[], documentSummaries: M.DocumentSummaries): M.DocumentSummaries {
      const relevant: number[] = members.filter((m) => this.relatedLimitation().ls.some((l) => l.includes(m)));
      const groups = documentSummaries.groups.filter((g) => {
        if (g.head) {
          return relevant.every((r) => g.selectionLabelIds.includes(r));
        }
        return false;
      })
        .filter((g) => !g.selectionLabelIds.every((l) => members.includes(l)))
        .filter(notEmpty);
      const ds: M.DocumentSummaries = { ...documentSummaries, groups };
      sortGroups(ds);
      return ds;
    },
    fileDocumentName(document: FileDocument): string {
      return fileDocumentName(document);
    },
    showDocument(document: FileDocument) {
      this.goto = { documentId: document.id };
    },
    summariesStats(documentSummaries: M.DocumentSummaries): Record<string, number> {
      const existingSummaries = documentSummaries.groups.map((g) => g.head).filter(notEmpty);
      const all = documentSummaries.groups.length;
      const approved = existingSummaries.filter((h) => h.approvals.find((a) => a.approved)).length;
      const disapproved = existingSummaries.filter((h) => h.approvals.find((a) => !a.approved)).length;
      const corrected = existingSummaries.filter((h) => h.summary.correction !== undefined).length;
      const short = documentSummaries.groups.filter((g) => isShortSummaryGroup(g)).length;
      const missing = all - existingSummaries.length - short;
      const pending = all - approved - disapproved - missing;
      return {
        approved,
        pending,
        missing,
        disapproved,
        corrected,
        short,
      };
    },
    filterSummaries(documentSummaries: M.DocumentSummaries): M.DocumentSummaries {
      const groups = documentSummaries.groups.filter((g) => {
        if (!this.filter.labels.every((l) => g.selectionLabelIds.includes(l))) {
          return false;
        }
        if (this.filter.status.length) {
          return this.filter.status.some((s) => (s === 'Approved' && this.isApproved(g.head))
          || (s === 'Pending' && g.head && !this.isApproved(g.head) && !this.isDisapproved(g.head))
          || (s === 'Missing' && !g.head)
          || (s === 'Disapproved' && this.isDisapproved(g.head))
          || (s === 'Corrected' && g.head?.summary?.correction)
          || (s === 'Short' && isShortSummaryGroup(g)));
        }
        return !isShortSummaryGroup(g);
      });
      return {
        ...documentSummaries,
        groups,
      };
    },
    filterLabel(label: M.Label) {
      if (this.filter.labels.includes(label.id)) {
        this.filter.labels = this.filter.labels.filter((l) => l !== label.id);
      } else {
        this.filter.labels.push(label.id);
      }
      this.storeFilter();
    },
    filterStatus(status: Status) {
      if (this.filter.status.includes(status)) {
        this.filter.status = this.filter.status.filter((s) => s !== status);
      } else {
        this.filter.status.push(status);
      }
      this.storeFilter();
    },
    storeFilter() {
      localStorage.setItem('summarySelectionView.filter', JSON.stringify(this.filter));
    },
    labelLookup(id: number): M.Label | undefined {
      return labels.getters.lookup.get(id);
    },
    isApproved(head: M.DocumentSummary | undefined): boolean {
      return head?.approvals.find((a) => a.approved) !== undefined;
    },
    isDisapproved(head: M.DocumentSummary | undefined): boolean {
      return head?.approvals.find((a) => !a.approved) !== undefined;
    },
    editDocument(item: FileDocument) {
      this.editItem = { ...item };
      this.edit = true;
    },
    saved(_: FileDocument) {
      this.load();
    },
    async checkForActiveBulkJobs() {
      if (!this.selectedDocuments.length) {
        return;
      }

      try {
        // Check all selected documents for active bulk jobs in parallel
        const allActiveJobs = await Promise.all(this.selectedDocuments.map((document) => api.getActiveBulkJobs(document.id)));

        // Find the first document with active jobs and store job IDs
        for (let i = 0; i < allActiveJobs.length; i++) {
          const activeJobs = allActiveJobs[i];
          const document = this.selectedDocuments[i];

          if (activeJobs.length > 0) {
            // Store the most recent job for this document
            const mostRecentJob = activeJobs.sort((a, b) => new Date(b.created).getTime() - new Date(a.created).getTime(),
            )[0];

            this.documentJobIds.set(document.id, mostRecentJob.id);

            // If we don't have a current job being tracked, track this one
            if (!this.bulkJob) {
              this.bulkJob = mostRecentJob;

              // Start subscribing to progress updates
              this.subscribeToBulkProgress();
            }
          }
        }
      } catch (error) {
        // Failed to check for active bulk jobs
        // eslint-disable-next-line no-console
        console.error('Failed to check for active bulk jobs:', error);
      }
    },

    openGenerateDialog(documentSummaries: M.DocumentSummaries) {
      this.selectedDocument = documentSummaries;
      this.generateDialog = true;
    },

    async confirmGenerate() {
      if (!this.selectedDocument) return;

      this.generateDialog = false;
      await this.generateAllSummaries(this.selectedDocument);
    },

    async generateAllSummaries(documentSummaries: M.DocumentSummaries) {
      // Don't start a new job if one is already running
      if (this.bulkProgress) {
        return;
      }

      try {
        // Filter groups based on options
        let filteredGroups = documentSummaries.groups.filter((group) => !isShortSummaryGroup(group));

        if (this.generateOptions.onlyMissing) {
          filteredGroups = filteredGroups.filter((group) => !group.head && this.availableSummary(group));
        }

        const specs: M.SummarySpec[] = filteredGroups.map((group) => ({
          members: group.selectionLabelIds.map((lid) => [lid]),
        }));

        // Initialize progress bar immediately
        this.bulkProgress = {
          isRunning: true,
          status: 'pending',
        };

        this.bulkJob = await api.generateBulkDocumentSummaries(
          documentSummaries.document.id,
          {
            specs,
            instructions: '',
            autoApprove: this.generateOptions.autoApprove,
          },
        );

        // Store the job ID associated with this document
        this.documentJobIds.set(documentSummaries.document.id, this.bulkJob.id);

        this.subscribeToBulkProgress();
      } catch (error) {
        this.bulkProgress = undefined;
      }
    },

    subscribeToBulkProgress() {
      if (!this.bulkJob) {
        return;
      }

      this.subscribeUntil((job: M.SerfJob) => {
        if (job.id !== this.bulkJob?.id) {
          return false;
        }

        this.bulkProgress = {
          isRunning: !job.stopped,
          status: job.status,
        };

        if (job.stopped) {
          if (this.bulkJob) {
            for (const [documentId, jobId] of this.documentJobIds.entries()) {
              if (jobId === this.bulkJob.id) {
                this.documentJobIds.delete(documentId);
                break;
              }
            }
          }

          this.load();
          return true;
        }
        return false;
      }).finally(() => {
        this.bulkProgress = undefined;
      });
    },

    availableSummary(group: M.SummaryGroup) {
      if (group.head) {
        return true;
      }
      const slices = group.specSlices || [];
      const chars = slices.map((s) => s.to - s.from)
        .reduce((a, b) => a + b, 0);
      return chars > 300;
    },
  },
});

const sortGroups = (ds: M.DocumentSummaries) => {
  const ls = labelSorter<M.Label>((l) => (l.type === 'Theme' ? ['type', 'natural'] : ['type', 'alpha']));
  ds.groups
    .sort((ag, bg) => {
      for (const [i, lid] of ag.selectionLabelIds.entries()) {
        const a = labels.getters.lookup.get(lid);
        const b = labels.getters.lookup.get(bg.selectionLabelIds[i]);
        if (!a || !b) {
          return 0;
        }
        const o = ls(a, b);
        if (o !== 0) {
          return o;
        }
      }
      return 0;
    });
};
